/**
 * languageName - turn a stored language value into something readable.
 *
 * Matters store a language CODE (`en`, `bg`, `sv`) and the UI was rendering it
 * raw, so the Context pane's Overview chip said "en" where it should say
 * "English". The jurisdiction resolver solved the same class of problem for
 * countries; this is its counterpart, and it lives beside it for the same
 * reason - four places displayed the language and each would otherwise grow
 * its own half-map.
 *
 * `Intl.DisplayNames` does the work: it is in every browser the app targets,
 * it knows every BCP-47 tag rather than the handful someone remembered, and
 * it localises the NAME too - a Bulgarian user sees "английски", not
 * "English". The fallback map covers the codes the product actually ships
 * with, for the case where `Intl.DisplayNames` is missing (older Safari, some
 * JSDOM setups) or returns the input unchanged for a tag it does not know.
 *
 * Anything unresolvable comes back as the raw value, prettified - never
 * blank, and never the literal "undefined".
 */

/** The product's own languages, for when `Intl.DisplayNames` cannot help. */
const FALLBACK_NAMES: Record<string, string> = {
  en: 'English',
  bg: 'Bulgarian',
  sv: 'Swedish',
  no: 'Norwegian',
  nb: 'Norwegian',
  nn: 'Norwegian',
  da: 'Danish',
  fi: 'Finnish',
  de: 'German',
  fr: 'French',
  nl: 'Dutch',
  es: 'Spanish',
  it: 'Italian',
  pt: 'Portuguese',
  pl: 'Polish',
  ro: 'Romanian',
  el: 'Greek',
  cs: 'Czech',
  sk: 'Slovak',
  sl: 'Slovenian',
  hr: 'Croatian',
  hu: 'Hungarian',
  et: 'Estonian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  mt: 'Maltese',
  ga: 'Irish',
  is: 'Icelandic',
  he: 'Hebrew',
  ar: 'Arabic',
  ru: 'Russian',
  uk: 'Ukrainian',
  tr: 'Turkish',
  zh: 'Chinese',
  ja: 'Japanese',
  ko: 'Korean',
};

/**
 * Values that are already a NAME rather than a code.
 *
 * Some rows store "English" or "Bulgarian" outright. Those must pass through
 * untouched - running them through `Intl.DisplayNames` would return the input
 * anyway, but checking first avoids depending on that behaviour.
 */
const KNOWN_NAMES = new Set(Object.values(FALLBACK_NAMES).map((n) => n.toLowerCase()));

/** `Intl.DisplayNames` is constructed once per UI locale, not per render. */
const displayNames = new Map<string, Intl.DisplayNames | null>();

function resolver(locale: string): Intl.DisplayNames | null {
  if (!displayNames.has(locale)) {
    try {
      // `type: 'language'` is what turns "en" into "English".
      displayNames.set(locale, new Intl.DisplayNames([locale], { type: 'language' }));
    } catch {
      displayNames.set(locale, null);
    }
  }
  return displayNames.get(locale) ?? null;
}

/**
 * The display name for a language value.
 *
 * @param value  a code (`en`, `en-GB`, `BG`) or an already-written name.
 * @param locale the UI language to render the name IN. Defaults to English so
 *               a caller that does not care gets a stable answer.
 */
export function languageName(
  value: string | null | undefined,
  locale = 'en'
): string {
  if (!value || typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw) return '';

  // Already a name - "English", "Bulgarian".
  if (KNOWN_NAMES.has(raw.toLowerCase())) {
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }

  // Codes are case-insensitive in our data ("EN" and "en" both occur), but
  // `Intl` wants the canonical lower-case tag.
  const tag = raw.toLowerCase().replace(/_/g, '-');
  const base = tag.split('-')[0];

  const intl = resolver(locale);
  if (intl) {
    try {
      const resolved = intl.of(tag);
      // `of()` echoes the input back for a tag it does not recognise, which
      // is not an answer - fall through to the map rather than show "en".
      if (resolved && resolved.toLowerCase() !== tag) return resolved;
    } catch {
      /* an invalid tag throws; the map and the raw value still apply */
    }
  }

  const mapped = FALLBACK_NAMES[base];
  if (mapped) return mapped;

  // Unknown: give back what was stored rather than nothing.
  return raw;
}

export default languageName;
